import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Sparkles, LogOut } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useReverbStatus } from '@/contexts/ReverbStatusContext';
import { resolveDisplayName } from '@/lib/display-name';
import { cn } from '@/lib/utils';
import { navItems, accountItems } from './navItems';

/**
 * AppHeader — the shell's only surface that renders at every viewport.
 *
 * Two jobs, and the second one is the reason it exists rather than a Sheet
 * bolted onto the sidebar.
 *
 * 1. It carries mobile navigation. Before Phase 15 the authenticated app had
 *    NO navigation below 1024px at all: `AppSidebar` is `hidden lg:flex` and
 *    nothing replaced it, so a phone user could reach a page only by typing
 *    its URL.
 *
 * 2. **It owns the connection dot, permanently and at every width.** The dot
 *    used to live inside the `lg`-only sidebar. `ws-live-smoke.spec.ts` pins
 *    `[data-testid="connection-dot"]` across five assertions, and the
 *    Playwright config sets no explicit viewport — so moving the dot into a
 *    Sheet would have made it absent from the DOM until the menu was opened
 *    and turned those five assertions red. Putting it here instead keeps it
 *    mounted unconditionally, which is strictly better than the shipped
 *    behaviour: it was previously missing below `lg` for real users too.
 *
 *    There is exactly ONE element carrying that testid in the tree. The
 *    sidebar renders the email without a dot for the same reason.
 */
export function AppHeader() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { status } = useReverbStatus();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Brand palette only (CLAUDE.md): sage = live, stone = polling fallback,
  // pulsing faint sage = still negotiating the first handshake.
  const dotClass =
    status === 'connected'
      ? 'bg-sage-500'
      : status === 'polling'
        ? 'bg-stone-400'
        : 'bg-sage-300 animate-pulse';
  const dotTitle =
    status === 'connected' ? 'Live' : status === 'polling' ? 'Polling for updates' : 'Connecting';

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/');
  }

  return (
    <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3 lg:px-8">
      {/* Mobile menu — the only nav below lg. */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          className="lg:hidden -ml-1 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('app.sidebar.open_menu')}
          data-testid="mobile-nav-trigger"
        >
          <Menu size={20} />
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0">
          <SheetTitle className="flex items-center gap-2 border-b border-border px-6 py-5 font-display text-lg text-foreground">
            <Sparkles className="size-5 text-primary" />
            becoming.you
          </SheetTitle>
          <nav className="space-y-1 px-3 py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )
                }
              >
                <item.icon size={18} />
                {t(item.i18nKey)}
              </NavLink>
            ))}
          </nav>
          {/* Account, below the flow and separated from it. The sidebar puts
              these in a dropdown under the identity line; a Sheet is already a
              drawer, so nesting a second one inside it would be a menu inside a
              menu. Same list, same source (`accountItems`), different shape. */}
          <div className="space-y-1 border-t border-border px-3 py-4">
            {user && (
              <p className="truncate px-3 pb-1 text-sm font-medium text-foreground">
                {resolveDisplayName(user)}
              </p>
            )}
            {accountItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )
                }
              >
                <item.icon size={18} />
                {t(item.i18nKey)}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <LogOut size={18} />
              {t('app.sidebar.sign_out')}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Wordmark, mobile only — the sidebar already carries it at lg. */}
      <span className="flex items-center gap-2 font-display text-base text-foreground lg:hidden">
        <Sparkles className="size-5 text-primary" />
        becoming.you
      </span>

      <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
        <span
          className={cn('size-2 shrink-0 rounded-full', dotClass)}
          aria-hidden="true"
          title={dotTitle}
          data-testid="connection-dot"
          data-status={status}
        />
        {user && (
          <span className="hidden truncate sm:inline">{resolveDisplayName(user)}</span>
        )}
      </div>
    </header>
  );
}
